import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { Tooltybe } from './tooltybe';

@Component({
  selector: 'app-toolbar-v1',
  templateUrl: './toolbar-v1.component.html',
  styleUrl: './toolbar-v1.component.css'
})
export class ToolbarV1Component implements OnInit {

  @Input()
  set tools(value: Tooltybe[]) {
    // ✅ Normalize legacy label/isLeft/disabledColor — do NOT evaluate functions.
    // hidden, disabled, isLoading are kept as-is (function or boolean)
    // so that evalFn() can re-evaluate them reactively on every CD cycle.
    this._tools = (value || []).map(tool => ({
      ...tool,
      label: tool.label || tool.lebel,
      isLeft: tool.isLeft ?? tool.isleft ?? false,
      disabledColor: tool.disabledColor || tool.desibleColor,
    }));
  }

  get tools(): Tooltybe[] {
    return this._tools;
  }

  /** Reactively evaluate a tool property: calls it if it's a function, else returns the value. */
  evalFn(val: any, defaultVal: any = false): any {
    return typeof val === 'function' ? val() : (val ?? defaultVal);
  }

  /**
   * Controls toolbar visibility:
   * - In VIEW mode: hides the toolbar if EVERY tool is hidden OR disabled.
   *   (At least one tool must be visible AND enabled to show the toolbar)
   * - In CREATE/UPDATE mode: always shows the toolbar (tools may be enabled).
   * - isDeletable overrides everything: if true, toolbar is always visible.
   */
  get hasVisibleTools(): boolean {
    // If the delete button is active, always show toolbar
    if (this.isDeletable) return true;
    if (!this._tools || this._tools.length === 0) return false;

    // In CREATE/UPDATE modes: show as long as there is at least one tool
    if (!this.isViewMode) return true;

    // In VIEW mode: hide if every tool is either hidden OR disabled
    return this._tools.some(
      tool => !this.evalFn(tool.hidden) && !this.evalFn(tool.disabled)
    );
  }

  @Input({ required: false }) isDeletable = false;
  @Input({ required: false }) dropdownLabel?: string;
  /** Pass true when the page is in VIEW mode to enable auto-hiding when no actionable button is available. */
  @Input({ required: false }) isViewMode: boolean = false;

  private _tools: Tooltybe[] = [];

  constructor(
    private translateService: TranslateService,
    private localizationService: LocalizationService
  ) {}

  get isRtl(): boolean {
    return this.localizationService.getCachedLanguage() === 'ar';
  }

  ngOnInit(): void {
    import('bootstrap/dist/js/bootstrap.bundle.min.js').then(module => {
      // module.default.Tooltip.init();
    });
  }

  getTooltip(tool: Tooltybe): string | null {
    if (tool.tooltip) return tool.tooltip;
    if (tool.label) return null;

    const icon = (tool.icon || '').toLowerCase();
    
    if (icon.includes('paperclip')) return 'GENERAL.ACTIONS.ATTACHED';
    if (icon.includes('edit') || icon.includes('pencil')) return 'GENERAL.ACTIONS.EDIT';
    if (icon.includes('trash')) return 'GENERAL.ACTIONS.DELETE';
    if (icon.includes('refresh') || icon.includes('sync')) return 'GENERAL.ACTIONS.RESET';

    return (!tool.label && tool.lebel) ? tool.lebel : null;
  }
}
